export const ACCEPTED_DOCUMENT_FILE_TYPES = {
  "application/pdf": "PDF",
  "text/plain": "TXT",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PPTX",
  "application/vnd.ms-powerpoint": "PPT",
  "text/csv": "CSV",
  "application/csv": "CSV",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "text/markdown": "MD",
  "audio/mpeg": "MP3",
  "audio/wav": "WAV",
  "audio/x-wav": "WAV",
  "video/mp4": "MP4",
  "video/x-msvideo": "AVI",
  "video/avi": "AVI",
  "video/quicktime": "MOV",
} as const;

export const ACCEPTED_DOCUMENT_FILE_EXTENSIONS = [
  "pdf",
  "txt",
  "docx",
  "csv",
  "xlsx",
  "md",
  "mp3",
  "wav",
  "mp4",
  "avi",
  "mov",
] as const;

export const SUPPORTED_DOCUMENT_FORMAT_LABELS = [
  "PDF",
  "DOCX",
  "CSV",
  "XLSX",
  "MD",
  "MP3",
  "WAV",
  "MP4",
  "AVI",
  "MOV",
  "TXT",
] as const;

export const MAX_DOCUMENT_FILE_SIZE = 100 * 1024 * 1024;
export const MAX_DOCUMENT_UPLOAD_FILES = 5;

/** Analyst file-manager upload-and-analyse-files: Max 20 files, 100MB each. */
export const MAX_ANALYST_UPLOAD_FILES = 20;
/** Analyst analyse-files endpoint: maxItems 5. */
export const MAX_ANALYST_ANALYSE_FILES = 5;

export function formatDocumentFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 Bytes";
  }

  const sizeBase = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(sizeBase));

  return `${parseFloat((bytes / Math.pow(sizeBase, sizeIndex)).toFixed(2))} ${
    sizes[sizeIndex]
  }`;
}

export function getDocumentFileExtension(fileName: string): string | null {
  const extension = fileName.split(".").pop()?.toLowerCase();
  return extension || null;
}

export function isAcceptedDocumentFile(file: File): boolean {
  if (
    file.type &&
    Object.prototype.hasOwnProperty.call(ACCEPTED_DOCUMENT_FILE_TYPES, file.type)
  ) {
    return true;
  }

  const extension = getDocumentFileExtension(file.name);
  if (!extension) {
    return false;
  }

  return ACCEPTED_DOCUMENT_FILE_EXTENSIONS.includes(
    extension as (typeof ACCEPTED_DOCUMENT_FILE_EXTENSIONS)[number]
  );
}

export function getDocumentFileAcceptValue(): string {
  const mimeTypes = Object.keys(ACCEPTED_DOCUMENT_FILE_TYPES);
  const extensions = ACCEPTED_DOCUMENT_FILE_EXTENSIONS.map(
    (extension) => `.${extension}`
  );

  return [...mimeTypes, ...extensions].join(",");
}

export function getDocumentFileValidationError(file: File): string | null {
  if (file.size > MAX_DOCUMENT_FILE_SIZE) {
    return `File size must be less than ${
      MAX_DOCUMENT_FILE_SIZE / 1024 / 1024
    }MB`;
  }

  if (!isAcceptedDocumentFile(file)) {
    const extension = getDocumentFileExtension(file.name);
    return extension
      ? `File type .${extension} is not supported`
      : "File type is not supported";
  }

  return null;
}

export function getDocumentFileTypeLabel(file: File): string {
  const mimeTypeLabel =
    ACCEPTED_DOCUMENT_FILE_TYPES[
      file.type as keyof typeof ACCEPTED_DOCUMENT_FILE_TYPES
    ];

  if (mimeTypeLabel) {
    return mimeTypeLabel;
  }

  const extension = getDocumentFileExtension(file.name);
  if (extension) {
    return extension.toUpperCase();
  }

  return "UNKNOWN";
}

interface DocumentUploadGroup {
  id: string;
  name: string;
  is_default?: boolean;
}

export function getDefaultDocumentGroupId(
  groups: DocumentUploadGroup[]
): string | null {
  if (groups.length === 0) {
    return null;
  }

  const defaultGroup = groups.find((group) => group.is_default === true);
  if (defaultGroup) {
    return defaultGroup.id;
  }

  const namedDefaultGroup = groups.find(
    (group) => group.name.trim().toLowerCase() === "default"
  );
  if (namedDefaultGroup) {
    return namedDefaultGroup.id;
  }

  return groups[0].id;
}
